import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TabView, TabPanel } from 'primereact/tabview';
import { toast } from "react-toastify";
import PaymentOrderHistory from "./PaymentOrderHistory";
import Loader from "../../Layout/Loader";
import PaymentOrderInfo from "./PaymentOrderInfo";
import PaymentOrderComments from "./PaymentOrderComments";
import PaymentBankAccounts from "./PaymentBankAccounts";
import "../open-banking.scss";
import { globalHandleChange } from "../../../services/functions";
import SummaryPaymentTable from "./PaymentOrderInvoiceSummary";
import PaymentOrderTotals from "./PaymentOrderTotals";
import { useHistory, useLocation } from 'react-router-dom';
import { defaultPaymentOrder, paymentOrderValid } from "./PaymentOrderUtils";
import { useSavePaymentOrder } from "./PaymentOrderService";
import DisabledLayover from "../../DisabledLayover";
import MobileInvoiceWizard from "../../CustomInvoices/DemoWizardSteps/MobileInvoiceWizard";

const CreatePaymentOrder = (props) => {
  const { t } = useTranslation("translations");
  const [paymentOrderAttachments, setPaymentOrderAttachments] = useState([]);
  const [validationErrors,setValidationErrors] = useState({})
  const history = useHistory()
  const location = useLocation();


  const [paymentOrder, setPaymentOrder] = useState(() => {
    if (location?.state?.paymentOrderRecords?.length > 0) {
      const { paymentOrderRecords, currency, sellerBankAccountTransactional } = location.state;
      return {
        ...defaultPaymentOrder,
        currency: currency || defaultPaymentOrder.currency,
        paymentOrderRecords: paymentOrderRecords,
        sellerBankAccountTransactional: sellerBankAccountTransactional || defaultPaymentOrder.sellerBankAccountTransactional,
      };
    }
    return defaultPaymentOrder;
  });


  const [reloadPage, setReloadPage] = useState(false);
  const [editBankAccountModal, setEditBankAccountModal] = useState({ show: false });

  const valid = ({validateOnlyEmployeesFields=false,excludeFields,updatedPayroll}={})=>{
    const result = paymentOrderValid({validateOnlyEmployeesFields,t,setValidationErrors,data:paymentOrder,excludeFields})
    return result 
  }
  const { savePaymentOrderFn,loading:paymentLoading }  = useSavePaymentOrder({valid,setEditBankAccountModal})


  const savePaymentOrder = () => {

    const attachments = paymentOrderAttachments.map(({source,...restAtt})=>({...restAtt}))

    savePaymentOrderFn({paymentOrder:{...paymentOrder,attachments},onSuccess:(res)=>{
      setPaymentOrderAttachments([])
      history.push("/edit-payment-order/" + res.id);
    }})

  };
  const handleChange = (e, selectKey, selectValue, length) => 
    globalHandleChange({ key: (e?.target?.name ?? selectKey), value: (e?.target?.value ?? selectValue), setState: setPaymentOrder, length });

  const staticButtons = (
    <>
      <button
        className={`btn btn-sm btn-primary mr-3`}
        disabled={paymentLoading}
        style={{ minWidth: "100px" }}
        onClick={() => savePaymentOrder()}
      >
        {paymentLoading ? <Loader size="xs" color="white" /> : t("common.save")}
      </button>
    </>
  );

  const refreshData = () => setReloadPage((state) => !state);

  const disabled = false;

  const infoSection = (
    <PaymentOrderInfo
      paymentOrder={paymentOrder}
      setPaymentOrder={setPaymentOrder}
      validationErrors={validationErrors}
      handleChange={handleChange}
      disabled={disabled}
      hasActions={true}
    />
  );

  const bankAccountsSection = (
    <PaymentBankAccounts
      paymentOrder={paymentOrder}
      setPaymentOrder={setPaymentOrder}
      validationErrors={validationErrors}
      setValidationErrors={setValidationErrors}
      handleChange={handleChange}
      refreshData={refreshData}
      disabled={disabled}
      editBankAccountModal={editBankAccountModal}
      setEditBankAccountModal={setEditBankAccountModal}
    />
  );

  return (
    <div className="demoInvoice open-banking mt-1">
      <div className="row">
        <div className="col-12 pt-0">
          <div className="card p-xl-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center invoice-toolbar">
              <div className="d-flex align-items-center flex-wrap invoice-toolbar-checks">
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0 invoice-toolbar-actions">
                <div className="actions d-flex flex-wrap gap-2">
                  {staticButtons}
                </div>
              </div>
            </div>

            <div className="my-2 border-top border-success"></div>

            {/* Desktop: two columns */}
            <div className="row d-none d-md-flex">
              <div className="col-12 col-md-7 border-right" style={{height:"100% !important"}}>
                {infoSection}
              </div>
              <div className="col-12 col-md-5" style={{height:"100% !important"}}>
                {bankAccountsSection}
              </div>
            </div>

            {/* Mobile: wizard tabs */}
            <div className="d-md-none">
              <MobileInvoiceWizard errors={validationErrors} steps={[
                {
                  label: t("paymentOrder.paymentOrderInfo"),
                  errorKeys: ["issueDateTime", "currency", "description"],
                  content: infoSection,
                },
                {
                  label: t("openBanking.bankAccounts"),
                  errorKeys: [/^buyerBankAccount/, /^sellerBankAccount/],
                  content: bankAccountsSection,
                },
              ]} />
            </div>

            <div className="row">
              <div className="col-12 py-0" style={{minHeight:120,maxHeight:400}}>
                <DisabledLayover disabled={!paymentOrder?.sellerBankAccountTransactional?.sellerNipt && (!paymentOrder?.paymentOrderRecords?.length || !paymentOrder?.paymentOrderRecords[0]?.docNumber)}>
                  <SummaryPaymentTable
                    defaultPaymentOrder={defaultPaymentOrder}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    paymentOrder={paymentOrder}
                    setPaymentOrder={setPaymentOrder}
                    valid={valid}
                  />
                </DisabledLayover>
              </div>
            </div>
            <div className="row align-items-start">
              <div className="col-12 col-md-6 border-left" >
                <TabView>
                  <TabPanel header={t("openBanking.attachmentsComments")}>
                    <PaymentOrderComments
                      paymentOrder={paymentOrder}
                      setPaymentOrder={setPaymentOrder}
                      handleChange={handleChange}
                      paymentOrderAttachments={paymentOrderAttachments}
                      setPaymentOrderAttachments={setPaymentOrderAttachments}
                      disabled={disabled}
                    />
                  </TabPanel>
                  <TabPanel header={t("openBanking.history")}>
                    <PaymentOrderHistory payment={paymentOrder} disabled={disabled} />
                  </TabPanel>
                </TabView>
              </div>
              <div className="col-12 col-md-6 border-r">
                <PaymentOrderTotals paymentOrder={paymentOrder} setPaymentOrder={setPaymentOrder}/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentOrder;

